
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function CircuitLoadTestingPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Circuit Load Testing',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Circuit load testing and electrical capacity analysis in Fresno, CA. Ensure your electrical system can safely handle your power needs.',
    url: `${siteUrl}/services/testing-certification/circuit-load-testing`,
  };

  const features = [
    {
      icon: 'ri-flashlight-line',
      title: 'Circuit Capacity Analysis',
      description:
        'Measure actual circuit loads and compare to safe capacity limits',
    },
    {
      icon: 'ri-tools-line',
      title: 'Panel Load Assessment',
      description:
        'Evaluate total electrical panel load and available capacity for new circuits',
    },
    {
      icon: 'ri-alert-line',
      title: 'Overload Detection',
      description:
        'Identify overloaded circuits that create fire risks and safety hazards',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Appliance Load Testing',
      description:
        'Test circuits serving major appliances to ensure adequate capacity',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Load Balancing',
      description:
        'Analyze circuit distribution and recommend load balancing improvements',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safety Verification',
      description:
        'Ensure circuits operate within safe limits to prevent overheating and fires',
    },
    {
      icon: 'ri-home-4-line',
      title: 'Expansion Planning',
      description:
        'Determine if your panel can support additions, renovations, or new appliances',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Detailed Load Report',
      description:
        'Comprehensive report with circuit loads, capacity, and upgrade recommendations',
    },
  ];

  const whatsIncluded = [
    'Licensed C-10 electrician testing',
    'Circuit load measurements',
    'Panel capacity assessment',
    'Voltage and amperage testing',
    'Overload identification',
    'Load balancing analysis',
    'Thermal imaging scans',
    'Safety hazard detection',
    'Upgrade recommendations',
    'Detailed written report',
  ];

  return (
    <>
      <SEOHead
        title="Circuit Load Testing Fresno CA | Electrical Load Analysis & Testing"
        description="Circuit load testing and electrical capacity analysis in Fresno, CA. Ensure your electrical system can safely handle your power needs. Licensed electricians available."
        canonical="/services/testing-certification/circuit-load-testing"
        keywords="circuit load testing Fresno, electrical load testing, circuit capacity testing, electrical panel load analysis"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Circuit Load Testing — Fresno, CA"
        heroSubtitle="Ensure your electrical system can safely handle your power needs"
        heroImage="https://public.readdy.ai/ai/img_res/47cb0d7ee8f9f63148b1bcb36762629f.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20electrical%20load%20testing%20equipment%20including%20clamp%20meter%20digital%20multimeter%20and%20thermal%20imaging%20camera%20on%20table%20next%20to%20electrical%20panel%20with%20circuit%20breakers%20and%20wiring%20visible%20bright%20interior%20setting&width=700&height=600&seq=load-test-fresno-001&orientation=portrait"
        fresnoContent="Fresno homeowners and businesses need circuit load testing to ensure electrical systems can safely handle power demands. Whether you're adding new appliances, planning a renovation, experiencing frequent breaker trips, or concerned about electrical capacity, our licensed electricians provide thorough load testing throughout Fresno County. We measure actual circuit loads, identify overloaded circuits, and determine if your panel has capacity for expansion — helping you avoid electrical fires and system failures."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'What is circuit load testing and why do I need it in Fresno?',
            answer:
              'Circuit load testing measures the actual electrical current (amperage) flowing through your circuits and compares it to safe capacity limits. You need load testing if you experience frequent breaker trips, plan to add major appliances, are renovating your Fresno home, or want to verify your electrical system can safely handle your power needs. Overloaded circuits cause fires, equipment damage, and electrical failures.',
          },
          {
            question: 'How much does circuit load testing cost in Fresno?',
            answer:
              'Circuit load testing in Fresno typically costs $200–$400 depending on the number of circuits tested and system complexity. Comprehensive panel load assessments for larger homes or commercial properties may cost more. We provide upfront pricing and detailed reports showing circuit loads, available capacity, and recommendations for your Fresno property.',
          },
          {
            question: 'What does circuit load testing in Fresno include?',
            answer:
              'Our Fresno circuit load testing includes measuring amperage on individual circuits, testing voltage levels, evaluating total panel load, identifying overloaded circuits, checking load distribution across phases, thermal imaging to detect overheating, and assessing available capacity for new circuits. You receive a detailed report with circuit loads, safety concerns, and upgrade recommendations.',
          },
          {
            question: 'How do I know if my circuits are overloaded in Fresno?',
            answer:
              'Signs of overloaded circuits include frequent breaker trips, flickering lights when appliances run, warm outlets or switches, burning smells, buzzing sounds, and dimming lights. Our load testing measures actual circuit loads and identifies overloaded circuits before they cause fires or damage. Many Fresno homes have overloaded circuits due to increased electrical demands from modern appliances and devices.',
          },
          {
            question: 'Can you determine if my Fresno home can support new appliances or additions?',
            answer:
              'Yes. Our panel load assessment determines your total electrical capacity and how much is currently used. We calculate whether your panel can support new appliances, EV chargers, additions, or renovations. If your Fresno home needs more capacity, we provide recommendations and estimates for panel upgrades or additional circuits.',
          },
          {
            question: 'How long does circuit load testing take in Fresno?',
            answer:
              'Most residential circuit load testing takes 1–2 hours depending on the number of circuits and system complexity. Commercial properties or comprehensive panel assessments may take longer. We schedule testing at your convenience and provide detailed reports within 24–48 hours for Fresno property owners.',
          },
        ]}
      />
    </>
  );
}
